import { useParams } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { calculateAnnualFinancialSummary,
   formatCurrency,
   getVal, 
   setBool, 
   isNegative,
   formatDate
} from '../../helpers/process_morningstar.js';

import {
  Box,
  Container,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableContainer,
  TableRow,
  Paper
} from "@mui/material";


export default function AnnualCFandIncome() {
  const tableRef = useRef(null);
  const [tables, setTables] = useState([]);
  const [error, setError] = useState(null);
  const [currentTableData, setCurrentTableData] = useState([]);
  const [Tax, setTax] = useState(getVal("selectedTax"));
  const [capRD, setCapRD] = useState(getVal("capRD"));
  const [includeOtherIncome, setIncludeOtherIncome] = useState(getVal("includeOtherIncome"));

  useEffect(() => {
    if (tableRef.current) {
      const el = tableRef.current;
      el.scrollTo({
        left: el.scrollWidth,
      });
    }
  }, [currentTableData]);  

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "capRD") setCapRD(JSON.parse(event.newValue));
      if (event.key === "includeOtherIncome") setIncludeOtherIncome(JSON.parse(event.newValue));
    };
  
    const handleCustomUpdate = () => {
      setCapRD(getVal("capRD"));
      setIncludeOtherIncome(getVal("includeOtherIncome"));
    };
  
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("localStorageUpdated", handleCustomUpdate);
  
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("localStorageUpdated", handleCustomUpdate);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const financialSummaryData = await calculateAnnualFinancialSummary(capRD, includeOtherIncome, Tax);
      setCurrentTableData(financialSummaryData);
    };

    fetchData();
  }, [capRD, includeOtherIncome, Tax]);

  const handleToggleTax = async () => {
      const newTax = !Tax;
      setTax(newTax);
      setBool("selectedTax", newTax);

      window.dispatchEvent(new Event("localStorageUpdated"));

      const updatedData = await calculateAnnualFinancialSummary(capRD, includeOtherIncome, newTax);
      updateFinancialSummaryTable(updatedData);
  };

  // Helper to update table data
  const updateFinancialSummaryTable = (newData) => {
    setTables(prev =>
      prev.map(t =>
        t.tableName === "Cash Flow & Income" ? { ...t, tableData: newData } : t
      )
    );
    setCurrentTableData(newData);
  };

  if (error) return <div>Error: {error}</div>;

      return (
        <>
            <>

              {currentTableData.length > 0 ? (
              <Box sx={{ mx: "25px" }}>
               <TableContainer component={Paper} sx={{ boxShadow: "none", pb: 10 }} ref={tableRef}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        position: "sticky",
                        left: 0,
                      }}
                    >
                  CF From Operations & Income 
                </Typography>                  
                 <Table stickyHeader size="small">
                  <TableHead>
                      {[{ value : "Date", time: "Time" }].map((item, idx) => (
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            backgroundColor: "#F9FAFB", 
                            borderRight: "1px solid #969696ff",
                            fontWeight: "bold", 
                            color: "black",
                            position: "sticky", 
                            left: 0, 
                            zIndex: 3, 
                          }}
                        >
                          Date
                        </TableCell>
                        {currentTableData.map((row, colIdx) => (
                          <TableCell sx={{backgroundColor: row[item.time] === "Annual" ? "#324052" : "#FFF176", 
                              borderRight: "1px solid #969696ff",
                              color: row[item.time] === "Annual" ? "white" : "black", 
                              whiteSpace: 'nowrap',
                              padding: 0,
                            }} 
                              key={colIdx}
                          >
                            <div style={{ textAlign: "center", fontSize: "0.67rem" }}>{row[item.time]}</div>
                            <div style={{ 
                              textAlign: "center",
                              fontWeight: "bold",
                              textDecoration: "underline",
                              fontSize: "0.67rem"
                              }}
                            >
                              {formatDate(row[item.value])}
                            </div>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableHead>
                   <TableBody>
                     {[
                      { label: "Tax Rate", field: "taxRate" },
                      { label: "Net Income from Total Operations", field: "netIncome" },
                      { label: "", field: "" },
                      { label: "Less: Non-Operating Income:", field: "" },
                      { label: "Interest Income", field: "interestIncome" },
                      { label: "Other Income, Net", field: "otherIncome" },
                      { label: "Income Restructuring And M&A", field: "incomeRest" },
                      { label: "Income Acquired in Process R&D", field: "incomeAcquired" },
                      { label: "Other Speacial Income/(Charges)", field: "specialIncome" },
                      { label: "Non-Operation Income", field: "nonOperationIncome" },
                      { label: "Non-Operation Income (After Tax)", field: "nonOperationIncomeAT" },
                      { label: "", field: "" },
                      { label: "Less: Other Income (Already reported net of tax)", field: "" },
                      { label: "Net Income from Discontinued Operations", field: "netIncomeFromDO" },
                      { label: "Net Income from Extraordinary Items", field: "netIncomeFromEI" },
                      { label: "Net Income Effect of Accounting Changes", field: "netIncomeFromAC" },
                      { label: "Net Income from Tax Loss Carryforward", field: "netIncomeFromTLC" },
                      { label: "Net Income from Other Gains (Losses)", field: "netIncomeFromOG" },
                      { label: "Total Other Income", field: "totalOtherIncome" },
                      { label: "", field: "" },
                      { label: "Plus: Interest Expense (After Tax)", field: "" },
                      { label: "Interest Expense", field: "interestExpense" },
                      { label: "Interest Expense (After Tax)", field: "interestExpenseAT" },
                      { label: "", field: "" },
                      { label: "Plus: Minority Interest", field: "" },
                      { label: "Minority Interest", field: "minorityInterests" },
                      { label: "", field: "" },
                      { label: "Plus: Depreciation & Amortization", field: "" },
                      { label: "Depreciation", field: "depreciation" },
                      { label: "Depreciation & Amortization", field: "depreciation" },
                      { label: "", field: "" },
                      { label: "Actual Taxes vs Calculated Taxes", field: "" },
                      { label: "Actual Taxes", field: "incomeTax" },
                      { label: "Calculated Taxes", field: "calculatedTax" },
                      { label: "Taxes User's Choice", field: "chosenTax" },
                      { label: "", field: "" },
                      { label: "Cash Flow from Operations", field: "cashFlowFromOps" },
                      { label: "Cash Flow from Operations (12 month period)", field: "cashFlowFromOps12M" },
                      { label: "Cash Flow from Operations (Annualized Q)", field: "cashFlowFromOpsAnnualized" },
                      { label: "", field: "" },
                      { label: "OR", field: "" },
                      { label: "", field: "" },
                      { label: "EBITDA", field: "ebitda" },
                      { label: "Less: Taxes Users' Choice", field: "chosenTax" },
                      { label: "Less: Tax Shield from Interest Expense", field: "taxShield" },
                      { label: "Cash Flow from Operations", field: "secondCashFlow" },
                      { label: "Cash Flow from Operations (12 month period)", field: "secondCashFlow12M" },
                      { label: "Cash Flow from Operations (Annualized Q)", field: "secondCashFlowAnnualized" },
                      { label: "EBITDA Per Share", field: "ebitdaPerShare" },
                      { label: "CF from Ops Per Share", field: "cfFromOpsPerShare" },
                      { label: "", field: "" },
                      { label: "Number of Periods with Data", field: "numOfPeriods" },
                      { label: "", field: "" },
                      { label: "CF - Interest (for Payout Ratio)", field: "cfInterest" },
                      { label: "CF - Interest (for Payout Ratio) / Share", field: "cfInterestPerShare" },

                    ].map((rowItem, rowIdx) => (
                      <TableRow
                        key={rowIdx}
                        sx={{
                          backgroundColor: rowIdx % 2 === 0 ? "#ffffff" : "#F9FAFB",
                          "& td": { fontSize: "0.67rem", borderBottom: "1px solid #E5E7EB", padding: "1px 8px" },
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {/* Render the row label as first cell */}
                        <TableCell sx={{ 
                          fontWeight: 'bold',
                          position: "sticky", 
                          left: 0, 
                          zIndex: 2,
                          height: 15,
                          backgroundColor: rowIdx % 2 === 0 ? "#ffffff" : "#F9FAFB",
                          borderRight: "1px solid #969696ff", 
                          }}
                        >
                          {rowItem.label}

                            {rowItem.label === "Actual Taxes vs Calculated Taxes" && (
                              <button
                                style={{
                                  fontSize: "0.67rem",
                                  padding: "2px 6px",
                                  backgroundColor: Tax ? "#4caf50" : "#e0e0e0",
                                  color: Tax ? "white" : "black",
                                  border: "none",
                                  borderRadius: "4px",
                                  cursor: "pointer"
                                }}
                                onClick={handleToggleTax}
                              >
                                Use Actual Taxes: {Tax ? "On" : "Off"}
                              </button>
                            )}
                        </TableCell>

                        {/* Render the financial data cells */}
                        {currentTableData.map((row, colIdx) => (
                          <TableCell key={colIdx} sx={{ 
                            borderRight: "1px solid #969696ff",
                            color: isNegative(row[rowItem.field]) ? "red" : "inherit",
                            textAlign: "right"
                          }}>
                            {formatCurrency(row[rowItem.field])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

              </TableContainer>
              </Box>
            ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "50vh", // full screen height
                    }}
                  >
                    <h3>Loading...</h3>
                    <CircularProgress />
                  </div>
            )}
        </>
    </>
  )}